// 当前激活的项目配置：运行/打包前由 scripts/prepare-project.ts 按 mode
// 从根目录 project-config/{mode}.ts 复制生成，故此处只需引入这一份。
import type { ProjectConfig } from './types';
import current from './current';

/* 项目 */
export enum ProjectEnum {
  /* 主站 */
  MAIN = 'm',

  /* 代理站 */
  DAILI = 'dl',

  /* 档口独立站 */
  SELLER = 'seller',

  /* 站点独立站 */
  SITE = 'site',
}

/* 项目构建类型 */
export enum ProjectBuildEnum {
  ALL = 'all',
  IOS = 'ios',
  ANDROID = 'android',
  HARMONY = 'harmony',
}

/** 应用升级配置 */
export interface UpgradeConfig {
  /** 是否启用更新检测（总开关） */
  enabled: boolean
  /** iOS App Store trackId（null = 未上架，不检测） */
  appleId: string | null
  /** iOS App Store 链接 */
  appStoreUrl: string
  /** 安卓检查更新接口（版本管理后台 latest 接口，含 appCode） */
  androidApiUrl: string
  /** 安卓应用包名（用于跳转应用市场） */
  androidPackageName: string
  /** 是否支持跳转安卓应用市场更新（false = 仅后台下载安装包） */
  supportMarketRedirect: boolean
  /** 版本管理后台应用标识（静态资源管理系统 appCode，下载页拉取各端下载地址用） */
  appCode?: string
}

/** 默认升级配置（禁用状态） */
const defaultUpgrade: UpgradeConfig = {
  enabled: false,
  appleId: null,
  appStoreUrl: '',
  androidApiUrl: '',
  androidPackageName: '',
  supportMarketRedirect: false,
  appCode: '',
};

/** 百度移动统计配置 */
export interface BaiduMtjConfig {
  /** 小程序 AppKey (mtj.baidu.com) */
  mpAppKey: string
  /** App AppKey */
  appAppKey: string
}

/** 默认百度移动统计配置（禁用状态） */
const defaultBaiduMtj: BaiduMtjConfig = {
  mpAppKey: '',
  appAppKey: '',
};

/** 第三方 SDK Key 配置（微信/QQ/高德），按项目差异化配置，用于生成 manifest 的 sdkConfigs */
export interface SdkConfig {
  /** 微信开放平台（分享 + 支付），appid 置空 = 不打入微信 SDK */
  weixin: { appid: string, universalLinks: string }
  /** QQ 互联（分享），appid 置空 = 不打入 QQ SDK */
  qq: { appid: string, universalLinks: string }
  /** 高德地图（已停用，Key 保持置空 = 不打入高德 SDK，仅保留系统定位） */
  amap: { name: string, appkey_ios: string, appkey_android: string }
}

/** 默认第三方 SDK 配置（全部置空 = 不打入任何第三方 SDK） */
const defaultSdk: SdkConfig = {
  weixin: { appid: '', universalLinks: '' },
  qq: { appid: '', universalLinks: '' },
  amap: { name: '', appkey_ios: '', appkey_android: '' },
};

const configInfo = (_VITE_APP_TYPE?: string, _VITE_APP_BUILD_TYPE?: string) => {
  // 项目已在运行/打包前由 prepare-project 选定并写入 ./current，
  // 因此不再按 VITE_APP_TYPE / VITE_APP_BUILD_TYPE 分支选择配置。
  // 显式标注 ProjectConfig：current.ts 为复制产物，字面量推断不含可选扩展字段
  const _configInfo: ProjectConfig = current;

  return {
    // 项目模式（打包隔离单位）与主题清单（见 src/themes）：
    // mode=ecom/shop/workbench；themes=该构建入包主题；theme=包默认主题
    mode: _configInfo.mode || 'ecom',
    theme: _configInfo.theme || '',
    themes: _configInfo.themes || [],
    mainColor: _configInfo.mainColor || '#dc1e1e',
    globalStyle: {
      'navigationStyle': 'default',
      'navigationBarTitleText': '',
      'navigationBarBackgroundColor': '#f8f8f8',
      'navigationBarTextStyle': 'black',
      'backgroundColor': '#FFFFFF',
      // App 专属属性放在 app-plus 内，避免写入 pages.json 顶层导致小程序报错
      // App 端：全局关闭页面回弹（橡皮筋效果）
      'app-plus': {
        bounce: 'none',
      },
      // 项目配置，支持覆盖
      ..._configInfo.globalStyle,
    },
    tabBar: {
      custom: false,
      color: '#999999',
      selectedColor: '#333333',
      backgroundColor: '#F8F8F8',
      borderStyle: 'black',
      height: '50px',
      fontSize: '10px',
      iconWidth: '24px',
      spacing: '3px',
      ..._configInfo.tabBar,
    },
    tabbarList: _configInfo.tabBar.list,
    pages: {
      exclude: ['**/components/**/**.*', '**/sections/**/**.*'],
      // pages 目录为 src/pages，分包目录也在 pages 目录下（如 pages/auth、pages/mine）
      // uni-helper 插件会自动扫描 subPackages 指定的子目录生成 pages.json 中的 subPackages 配置
      subPackages: [
        'src/pages/auth',
        'src/pages/mine',
        'src/pages/common',
        'src/pages/error',
        'src/pages/order',
        'src/pages/cloud',
        'src/pages/im',
        'src/pages/market',
        'src/pages/factory',
        'src/pages/logistics',
      ],
      dts: 'src/types/uni-pages.d.ts',
      // 项目配置，支持覆盖
      ..._configInfo.pages,
    },
    // 分包配置（用于pages.config.ts）
    // 只声明分包 root，具体页面由 UniPages 插件自动扫描 definePage() 生成
    // 避免显式声明页面路径导致与自动扫描合并产生重复或错误路径（如 ../../login）
    // 支持项目配置覆盖（模式隔离：不同模式可按需裁剪分包）
    subPackages: _configInfo.subPackages || [
      {
        root: 'pages/auth',
        pages: [],
      },
      {
        root: 'pages/mine',
        pages: [],
      },
      {
        root: 'pages/common',
        pages: [],
      },
      {
        root: 'pages/order',
        pages: [],
      },
      {
        root: 'pages/error',
        pages: [],
      },
      {
        root: 'pages/cloud',
        pages: [],
      },
      {
        root: 'pages/im',
        pages: [],
      },
      {
        root: 'pages/market',
        pages: [],
      },
      {
        root: 'pages/logistics',
        pages: [],
      },
    ],
    // 升级配置（透传到运行时）
    upgrade: _configInfo.upgrade || defaultUpgrade,
    // 百度移动统计配置
    baiduMtj: _configInfo.baiduMtj || defaultBaiduMtj,
    // 第三方 SDK Key 配置
    sdk: _configInfo.sdk || defaultSdk,
  };
};

export const getProjectConfig = (env: any) => {
  const { VITE_APP_TYPE, VITE_APP_BUILD_TYPE } = env;
  // console.log(env, '当前项目环境：', VITE_APP_TYPE, ', 构建内容：', VITE_APP_BUILD_TYPE);
  return configInfo(VITE_APP_TYPE, VITE_APP_BUILD_TYPE);
};

/**
 * 运行时获取当前项目的升级配置
 * 基于 import.meta.env 中的 VITE_APP_TYPE / VITE_APP_BUILD_TYPE 判断
 */
export function getUpgradeConfig(): UpgradeConfig {
  const env = import.meta.env;
  const config = configInfo(env.VITE_APP_TYPE, env.VITE_APP_BUILD_TYPE);
  return config.upgrade;
}

/**
 * 运行时获取当前项目的百度移动统计配置
 * 基于 import.meta.env 中的 VITE_APP_TYPE / VITE_APP_BUILD_TYPE 判断
 */
export function getBaiduMtjConfig(): BaiduMtjConfig {
  const env = import.meta.env;
  const config = configInfo(env.VITE_APP_TYPE, env.VITE_APP_BUILD_TYPE);
  return config.baiduMtj;
}

/**
 * 运行时获取当前项目的第三方 SDK 配置
 * 基于 import.meta.env 中的 VITE_APP_TYPE / VITE_APP_BUILD_TYPE 判断
 */
export function getSdkConfig(): SdkConfig {
  const env = import.meta.env;
  const config = configInfo(env.VITE_APP_TYPE, env.VITE_APP_BUILD_TYPE);
  return config.sdk;
}

/**
 * 是否为工作台模式（当前仅 m_ios = 主站 iOS 包使用，见 project-config/m_ios.ts）。
 * iOS 包不提供上货与积分购买：相关入口整包不渲染，路由层兜底提示引导电脑端。
 */
export function isWorkbenchMode(): boolean {
  return (current.mode || 'ecom') === 'workbench';
}
